import { useSyncExternalStore } from "react";

/** "all" = the aggregate PO-arrivals cell: both open and simulated rows. */
export type PoCellKind = "open" | "simulated" | "all";

export interface PoCellPopoverTarget {
  /** The clicked cell element — popover anchor and outline host. */
  anchorEl: HTMLElement;
  sku: string;
  /** null = SKU-level (match across all locations). */
  location: string | null;
  /** Epoch ms of the clicked date column (the matrix period start). */
  periodStartMs: number;
  kind: PoCellKind;
  /**
   * Which date the period filter uses. "delivery" for arrival cells (purchase
   * order arrivals / open / simulated); "placement" for "purchase order to
   * place" (the recommended order_placed_date).
   */
  bucketBy: "delivery" | "placement";
}

// Module-level store so matrix cells can open the popover without threading
// props (or state) through MUIFlatTable / the chunked table builder. Opening
// and closing re-renders ONLY subscribers (the popover itself) — the table's
// props never change, so react-virtuoso repaints zero cells.
let currentTarget: PoCellPopoverTarget | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function openPoCellPopover(target: PoCellPopoverTarget): void {
  currentTarget = target;
  emit();
}

export function closePoCellPopover(): void {
  if (currentTarget === null) return;
  currentTarget = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): PoCellPopoverTarget | null {
  return currentTarget;
}

export function usePoCellPopoverTarget(): PoCellPopoverTarget | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}
